import React, { memo, useCallback, useMemo, useState } from "react";
import { FiChevronDown, FiPlus, FiTrash2 } from "react-icons/fi";
import { supabase } from "../../config/supabase";


const PRICING_FIELDS = [
    { key: "roof", label: "Roof Trusses", departments: ["roof"] },
    { key: "labour", label: "Labour", departments: ["roof"] },

    { key: "first_floor", label: "1st Floor Trusses", departments: ["floor"] },
    { key: "second_floor", label: "2nd Floor Trusses", departments: ["floor"] },

    { key: "gf_wall", label: "Ground Floor Walls", departments: ["wall"] },
    { key: "first_floor_wall", label: "1st Floor Walls", departments: ["wall"] },
    { key: "second_floor_wall", label: "2nd Floor Walls", departments: ["wall"] },

    { key: "beams", label: "Beams", departments: ["beams"] },
    { key: "flooring_sheets", label: "Flooring Sheets", departments: ["flooring_sheets"] },
];

const DEFAULT_ENABLED_PRICING_FIELDS = PRICING_FIELDS.map(field => field.key);

const BASE_PRICING_FIELD_KEYS = PRICING_FIELDS.map(field => field.key);

const isCustomPricingField = (fieldKey) => {
    return !BASE_PRICING_FIELD_KEYS.includes(fieldKey);
};

const formatCurrencyDisplay = (value) => {
    if (value === "" || value === null || value === undefined) return "";

    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) return "";

    return `$${new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numberValue)}`;
};

const parseCurrencyInput = (value) => {
    const cleaned = String(value || "")
        .replace(/[^\d.,]/g, "")
        .replace(/\./g, "")
        .replace(",", ".");

    if (!cleaned) return "";

    const numberValue = Number(cleaned);
    if (Number.isNaN(numberValue)) return "";

    return String(numberValue);
};

const PricingMoneyInput = memo(({ value, onValueChange }) => {
    const [localValue, setLocalValue] = useState(formatCurrencyDisplay(value));
    const [focused, setFocused] = useState(false);

    React.useEffect(() => {
        if (!focused) setLocalValue(formatCurrencyDisplay(value));
    }, [value, focused]);

    return (
        <input
            type="text"
            inputMode="decimal"
            value={localValue}
            onFocus={() => {
                setFocused(true);
                setLocalValue(value || "");
            }}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={() => {
                setFocused(false);
                const parsed = parseCurrencyInput(localValue);
                onValueChange(parsed);
                setLocalValue(formatCurrencyDisplay(parsed));
            }}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="$0,00"
        />
    );
});

const EstimatorPricingForm = ({
    assignment,
    company,
    pricingValues,
    setPricingValues,
    required = false,
}) => {
    const [unitIds, setUnitIds] = useState([1]);
    const [openUnits, setOpenUnits] = useState({ 1: true });
    const [pricingEntries, setPricingEntries] = useState([]);

    const pricingSettings = company?.settings?.pricing_settings || {};

    const enabledPricingFieldKeys =
        pricingSettings.enabled_pricing_fields || DEFAULT_ENABLED_PRICING_FIELDS;

    React.useEffect(() => {
        if (!assignment?.id || !company?.id) return;

        const loadPricingEntries = async () => {
            const { data, error } = await supabase
                .from("job_pricing_entries")
                .select("id,type,department,unit_number,quote_revision,is_archived,custom_price_labels")
                .eq("company_id", company.id)
                .eq("job_id", assignment.id)
                .eq("is_archived", false);

            if (error) {
                console.error("Failed to load custom pricing fields:", error);
                return;
            }

            setPricingEntries(data || []);
        };

        loadPricingEntries();
    }, [assignment?.id, company?.id]);

    const hasDepartment = useCallback(
        (department) =>
            assignment?.job_quotes?.some(q => q.is_required && q.quote_type === department),
        [assignment?.job_quotes]
    );

    const customPricingFields = useMemo(() => {
        const rows = pricingEntries;

        const labels = {};

        rows
            .filter(row => row.type === "department" && row.custom_price_labels)
            .forEach(row => {
                Object.entries(row.custom_price_labels || {}).forEach(([key, label]) => {
                    if (!label) return;

                    labels[key] = {
                        key,
                        label,
                        is_custom: true,
                        departments: [row.department],
                    };
                });
            });

        return Object.values(labels).filter(field =>
            field.departments.some(dep => hasDepartment(dep))
        );
    }, [assignment?.job_pricing_entries, hasDepartment]);

    const enabledPricingFields = useMemo(
        () => [
            ...PRICING_FIELDS.filter(field =>
                enabledPricingFieldKeys.includes(field.key) &&
                field.departments.some(dep => hasDepartment(dep))
            ),
            ...customPricingFields,
        ],
        [hasDepartment, enabledPricingFieldKeys, customPricingFields]
    );

    const setPricingValue = useCallback((unitNumber, key, value) => {
        setPricingValues(prev => ({
            ...prev,
            [unitNumber]: {
                ...(prev[unitNumber] || {}),
                [key]: value,
            },
        }));
    }, [setPricingValues]);

    const addUnit = () => {
        setUnitIds(prev => {
            const nextId = prev.length ? Math.max(...prev) + 1 : 1;
            setOpenUnits(openPrev => ({ ...openPrev, [nextId]: true }));
            return [...prev, nextId];
        });
    };

    const removeUnit = (unitNumber) => {
        setUnitIds(prev => prev.filter(id => id !== unitNumber));

        setPricingValues(prev => {
            const next = { ...prev };
            delete next[unitNumber];
            return next;
        });

        setOpenUnits(prev => {
            const next = { ...prev };
            delete next[unitNumber];
            return next;
        });
    };

    const toggleUnit = (unitNumber) => {
        setOpenUnits(prev => ({
            ...prev,
            [unitNumber]: !prev[unitNumber],
        }));
    };

    const getFilledCount = (unitNumber) => {
        const values = pricingValues?.[unitNumber] || {};

        return enabledPricingFields.filter(field =>
            String(values?.[field.key] || "").trim() !== ""
        ).length;
    };

    if (!enabledPricingFields.length) return null;

    return (
        <div className="rounded-2xl bg-white border border-gray-200 p-4">
            <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                    <h5 className="text-sm font-black text-zinc-800">
                        Pricing Entry
                    </h5>
                    <p className="text-xs text-gray-500 mt-1">
                        Add units manually and enter pricing for each unit.
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    {required && (
                        <span className="rounded-full bg-red-50 border border-red-100 px-2 py-1 text-[11px] font-black text-red-600">
                            Required
                        </span>
                    )}

                    <button
                        type="button"
                        onClick={addUnit}
                        className="inline-flex items-center gap-1 rounded-lg bg-zinc-900 text-white px-3 py-1.5 text-xs font-bold hover:bg-zinc-800"
                    >
                        <FiPlus className="w-3.5 h-3.5" />
                        Add Unit
                    </button>
                </div>
            </div>

            <div className="space-y-3">
                {unitIds.map((unitNumber, index) => {
                    const isOpen = openUnits[unitNumber] !== false;
                    const filledCount = getFilledCount(unitNumber);
                    const isFilled = filledCount > 0;
                    const fullyFilled = filledCount === enabledPricingFields.length;

                    return (
                        <div
                            key={unitNumber}
                            className={`rounded-xl border overflow-hidden ${fullyFilled
                                ? "border-green-200 bg-green-50"
                                : isFilled
                                    ? "border-orange-200 bg-orange-50"
                                    : required
                                        ? "border-red-200 bg-red-50"
                                        : "border-gray-100 bg-gray-50"
                                }`}
                        >
                            <div className="w-full flex items-center justify-between gap-3 px-4 py-3">
                                <button
                                    type="button"
                                    onClick={() => toggleUnit(unitNumber)}
                                    className="flex items-center gap-2 text-left"
                                >
                                    <FiChevronDown
                                        className={`w-4 h-4 text-gray-500 ${isOpen ? "rotate-0" : "-rotate-90"
                                            }`}
                                    />

                                    <p className="text-sm font-black text-zinc-800">
                                        Unit {index + 1}
                                    </p>
                                </button>

                                <div className="flex items-center gap-2">
                                    {!isOpen && (
                                        <span
                                            className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${fullyFilled
                                                ? "bg-green-100 text-green-700 border-green-200"
                                                : isFilled
                                                    ? "bg-orange-100 text-orange-700 border-orange-200"
                                                    : "bg-red-100 text-red-700 border-red-200"
                                                }`}
                                        >
                                            {isFilled
                                                ? `Filled ${filledCount} / ${enabledPricingFields.length}`
                                                : "Not filled"}
                                        </span>
                                    )}

                                    {unitIds.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeUnit(unitNumber)}
                                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-100"
                                            title="Remove unit"
                                        >
                                            <FiTrash2 className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {isOpen && (
                                <div className="border-t border-white/70 p-3">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                        {enabledPricingFields.map(field => (
                                            <div key={`${unitNumber}-${field.key}`}>
                                                <label className="block text-xs font-bold text-gray-500 mb-1">
                                                    {field.label}
                                                </label>

                                                <PricingMoneyInput
                                                    value={pricingValues?.[unitNumber]?.[field.key] || ""}
                                                    onValueChange={(nextValue) =>
                                                        setPricingValue(unitNumber, field.key, nextValue)
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default EstimatorPricingForm;